import { z } from 'zod';
import { IsoDateTime, Uuid } from './common.js';
/**
 * Notices and the notice library (module `notices`, docs/modules/notices.md). Owner: task S4.
 * Notice text is the CloudSign notice-text format (a small Markdown subset, parsed by ../text/notice-text.ts).
 */
const Sha256Hex = z.string().regex(/^[0-9a-f]{64}$/);
export const NOTICE_BODY_MAX = 20000;
export const NoticeStatus = z.enum(['active', 'archived']);
export const NoticeUse = z.object({ siteId: Uuid, siteName: z.string(), groupId: Uuid, groupName: z.string() });
export const Notice = z.object({
    id: Uuid,
    title: z.string(),
    currentVersionId: Uuid,
    currentVersion: z.number().int(),
    requiresAcceptance: z.boolean(),
    requireSignature: z.boolean(),
    status: NoticeStatus,
    /** Where the notice is assigned (the library's "where used"). */
    usedIn: z.array(NoticeUse),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
/** Versions are immutable. A change of title or body makes a new version; acceptances point at the version shown. */
export const NoticeVersion = z.object({
    id: Uuid,
    noticeId: Uuid,
    version: z.number().int(),
    title: z.string(),
    /** Notice-text format, line endings normalised to \n. */
    body: z.string(),
    /** SHA-256 (hex) of title + "\n\n" + body as UTF-8. The kiosk sends the hash of what it showed. */
    contentSha256: Sha256Hex,
    createdAt: IsoDateTime,
});
export const NoticeCreate = z.object({
    title: z.string().trim().min(1).max(120),
    body: z.string().max(NOTICE_BODY_MAX),
    requiresAcceptance: z.boolean().default(true),
    requireSignature: z.boolean().default(false),
});
export const NoticeUpdate = z.object({
    title: z.string().trim().min(1).max(120).optional(),
    body: z.string().max(NOTICE_BODY_MAX).optional(),
    requiresAcceptance: z.boolean().optional(),
    requireSignature: z.boolean().optional(),
    status: NoticeStatus.optional(),
});
/** PUT /notices/assignments: the ordered notices for one site + group (replaces that list). */
export const NoticeAssignmentsPut = z.object({
    siteId: Uuid,
    groupId: Uuid,
    noticeIds: z.array(Uuid).max(20),
});
export const NoticeAssignment = z.object({ siteId: Uuid, groupId: Uuid, noticeId: Uuid, sortOrder: z.number().int() });
/** One notice the person accepted, as sent by the kiosk (or portal) with a sign-in. */
export const NoticeAcceptanceInput = z.object({
    noticeVersionId: Uuid,
    acceptedAt: IsoDateTime,
    /** Hash of the content the device displayed; must equal the version's contentSha256. */
    shownSha256: Sha256Hex,
    /** Device upload id of the signature strokes (ADR 0010), when the notice asks for a signature. */
    signatureUploadId: Uuid.nullable().default(null),
});
/** Kiosk step config for the `notices` step. */
export const KioskNoticesStepConfig = z.object({
    notices: z.array(NoticeVersion.pick({ id: true, noticeId: true, version: true, title: true, body: true, contentSha256: true }).extend({
        requiresAcceptance: z.boolean(),
        requireSignature: z.boolean(),
    })),
});
/** Stored acceptance as shown on a visit. */
export const NoticeAcceptance = z.object({
    id: Uuid,
    visitId: Uuid,
    noticeId: Uuid,
    noticeVersionId: Uuid,
    version: z.number().int(),
    title: z.string(),
    acceptedAt: IsoDateTime,
    signatureFileId: Uuid.nullable(),
});
//# sourceMappingURL=notices.js.map